import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Modal,
    InputAdornment,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ContentCopy } from '@mui/icons-material';
import styles from './style';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { LoadingButton } from '@mui/lab';
import color from "../../constants/color";


export default function RefundModal({
    open = false,
    onClose = () => { },
    refundData = {},
    handleRefundSubmit = () => { },
    bankData = [],
    editingRefund = null
}) {
    const [formData, setFormData] = React.useState({
        amount: '',
        refundedOn: dayjs().format('YYYY-MM-DD'),
        paymentMode: '',
        fromAccount: '',
        customerName: refundData.customer?.name || '',
        creditNoteNumber: refundData.creditNoteNumber || '',
        balance: refundData.balance?.toString() || '0',
    });

    useEffect(() => {
        if (editingRefund) {
            setFormData(prev => ({
                ...prev,
                amount: editingRefund.amount?.toString() || '',
                refundedOn: editingRefund.date ? dayjs(editingRefund.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                paymentMode: editingRefund.paymentMethod === 'Bank' ? 'Bank Transfer' : 'Cash',
                fromAccount: editingRefund.account || '',
                customerName: refundData.customer?.name || '',
                creditNoteNumber: refundData.creditNoteNumber || '',
                balance: refundData.balance?.toString() || '0',
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                amount: '',
                refundedOn: dayjs().format('YYYY-MM-DD'),
                paymentMode: '',
                fromAccount: '',
                customerName: refundData.customer?.name || '',
                creditNoteNumber: refundData.creditNoteNumber || '',
                balance: refundData.balance?.toString() || '0',
            }));
        }
    }, [editingRefund, open, refundData]);

    React.useEffect(() => {
        if (refundData && Object.keys(refundData).length > 0) {
            setFormData(prev => ({
                ...prev,
                customerName: refundData.customer?.name || '',
                creditNoteNumber: refundData.creditNoteNumber || '',
                balance: refundData.balance?.toString() || '0',
                amount: refundData.balance?.toString() || ''
            }));
        }
    }, [refundData]);

    const handleChange = (field) => (event) => {
        const value = event.target.value;

        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
            ...(field === "paymentMode" && value === "Cash" && { fromAccount: "" })
        }));
    };

    const isFormValid = () => {
        return (
            formData.amount &&
            formData.refundedOn &&
            formData.paymentMode &&
            (formData.paymentMode !== 'Bank Transfer' || formData.fromAccount)
        );
    };
    const handleSubmit = () => {
        const payload = {
            paymentMethod: formData.paymentMode === 'Bank Transfer' ? 'Bank' : 'Cash',
            account: formData.fromAccount,
            amount: parseFloat(formData.amount),
            date: new Date(formData.refundedOn).toISOString(),
            ...(editingRefund && { transactionId: editingRefund.transactionId })
        };

        handleRefundSubmit(
            refundData._id,
            payload,
            editingRefund ? 'update' : 'create'
        );
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="refund-modal-title"
            aria-describedby="refund-modal-description"
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
            }}
            sx={styles.modalBackdrop}
        >
            <Box sx={styles.modal}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '27px' }}>
                    <Typography
                        id="refund-modal-title"
                        variant="h6"
                        component="h2"
                        sx={styles.modalTitle}
                    >
                        {editingRefund ? 'Edit Refund' : 'Create Refund'}
                    </Typography>
                    <IconButton sx={{ width: 40, height: 40 }} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={styles.customerSection}>
                    <Box sx={styles.customerAvatar} aria-hidden="true" />
                    <Box sx={styles.customerInfo}>
                        <Typography sx={styles.customerLabel}>
                            Customer Name
                        </Typography>
                        <Typography sx={styles.customerValue}>
                            {formData.customerName}
                        </Typography>
                    </Box>
                    <ContentCopy sx={styles.copyIcon} />
                    <Box sx={styles.creditNoteInfo}>
                        <Typography sx={styles.customerLabel}>
                            Credit Note Number
                        </Typography>
                        <Typography sx={styles.customerValue}>
                            {formData.creditNoteNumber}
                        </Typography>
                    </Box>
                </Box>

                <Box component="form" noValidate autoComplete="off" sx={styles.form}>
                    <Box sx={styles.formRow}>
                        <Box sx={{ ...styles.field, flex: 1 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Typography sx={styles.label}>Refunded On</Typography>
                                <DatePicker
                                    value={dayjs(formData.refundedOn)}
                                    onChange={(newValue) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            refundedOn: dayjs(newValue).format('YYYY-MM-DD')
                                        }));
                                    }}
                                    sx={styles.formGroupFlex}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            required: true,
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <FormControl size="small" sx={styles.formGroupFixed}>
                            <Box sx={{ ...styles.field }}>
                                <Typography sx={styles.label}>Payment Mode</Typography>
                                <Select
                                    value={formData.paymentMode}
                                    labelId="payment-mode-label"
                                    id="payment-mode"
                                    onChange={handleChange('paymentMode')}
                                    sx={{
                                        ...styles.selectField,
                                        '& MuiInputBase-root': {
                                            width: '100%',
                                        },
                                        height: 39.5, // outer height of the text field
                                        "& .MuiInputBase-root": {
                                        height: "100%",
                                        fontSize: "1rem",
                                        },
                                        "& .MuiInputBase-input": {
                                        height: "100%",
                                        padding: "12px 14px", // control vertical padding for input
                                        boxSizing: "border-box",
                                        },
                                    }}
                                    displayEmpty
                                    renderValue={
                                        formData.paymentMode !== "" ? 
                                        undefined : 
                                        () => (
                                            <span style={{ color: color.lightText }}>
                                                Payment Mode
                                            </span>
                                        )
                                    }
                                >
                                    <MenuItem value="Cash" sx={styles.menuItem}>Cash</MenuItem>
                                    <MenuItem value="Bank Transfer" sx={styles.menuItem}>Bank</MenuItem>
                                </Select>
                            </Box>
                        </FormControl>
                    </Box>

                    <Box sx={styles.formRow}>
                        <Box sx={styles.field}>
                            <Typography sx={styles.label}>Amount</Typography>
                            <TextField
                                value={formData.amount}
                                placeholder='Amount'
                                onChange={handleChange('amount')}
                                size="small"
                                sx={styles.formGroupFlex}
                                type="number"

                            />
                        </Box>
                        <FormControl size="small" sx={styles.formGroupFixed}>
                            <Box sx={{ ...styles.field, width: '100%' }}>
                                <Typography sx={styles.label}>From Account</Typography>
                                <Select
                                    value={formData.fromAccount}
                                    onChange={handleChange('fromAccount')}
                                    sx={{
                                        ...styles.selectField,
                                        height: 39.5, // outer height of the text field
                                        "& .MuiInputBase-root": {
                                        height: "100%",
                                        fontSize: "1rem",
                                        },
                                        "& .MuiInputBase-input": {
                                        height: "100%",
                                        padding: "12px 14px", // control vertical padding for input
                                        boxSizing: "border-box",
                                        },
                                    }}
                                    disabled={formData.paymentMode !== 'Bank Transfer'}
                                    displayEmpty
                                    renderValue={
                                        formData.fromAccount !== "" ? 
                                        undefined : 
                                        () => (
                                            <span style={{ color: color.lightText }}>
                                                From Account
                                            </span>
                                        )
                                    }
                                >
                                    {bankData?.map((bank) => (
                                        <MenuItem key={bank._id} value={bank._id}>
                                            {`${bank?.bankName} - ${bank.accountNumber?.slice(-4)}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={styles.modalButtons}>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!isFormValid()}
                        sx={styles.jobButtons}
                    >
                        {editingRefund ? 'Update Refund' : 'Create Refund'}
                    </LoadingButton>
                </Box>
            </Box >
        </Modal >
    );
}